import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload, App } from "../components/admin-ui";
import { LoadingOutlined, PlusOutlined } from "./admin-icons";
import { useAuthStore } from "../store/auth";
import { BASE } from "../api/client";

interface Props {
  value?: string;
  onChange?: (url: string) => void;
  folder?: string;
  hint?: string;
}

export function ImageUpload({ value, onChange, folder = "menu", hint }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { message } = App.useApp();
  const authToken = useAuthStore((s) => s.accessToken);

  const beforeUpload = (file: File) => {
    const ok = file.type.startsWith("image/");
    if (!ok) { void message.error(t("imageUpload.imagesOnly")); }
    const small = file.size / 1024 / 1024 < 5;
    if (!small) { void message.error(t("imageUpload.maxSize")); }
    return ok && small;
  };

  const customUpload = async ({ file, onSuccess, onError }: {
    file: File | string | Blob;
    onSuccess?: (body: unknown) => void;
    onError?: (err: Error) => void;
  }) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file as File);
      fd.append("folder", folder);

      const res = await fetch(`${BASE}/api/v1/admin/uploads/image`, {
        method: "POST",
        body: fd,
        headers: {
          "Authorization": `Bearer ${authToken || ""}`,
        },
        signal: AbortSignal.timeout(20000),
      });

      if (!res.ok) {
        throw new Error(`Upload failed with status ${res.status}`);
      }

      const json = await res.json() as { data: { url: string } };
      onChange?.(json.data.url);
      onSuccess?.(json);
    } catch (err) {
      onError?.(err as Error);
      void message.error(t("imageUpload.uploadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (info: { file: { status?: string } }) => {
    if (info.file.status === "uploading") setLoading(true);
    if (info.file.status !== "uploading") setLoading(false);
  };

  return (
    <div>
      <Upload
        listType="picture-card"
        showUploadList={false}
        beforeUpload={beforeUpload}
        customRequest={customUpload as never}
        onChange={handleChange}
      >
        {value ? (
          <img src={value} alt={t("imageUpload.previewAlt")} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6 }} />
        ) : (
          <div>
            {loading ? <LoadingOutlined /> : <PlusOutlined />}
            <div style={{ marginTop: 8, fontSize: 12 }}>{t("imageUpload.upload")}</div>
          </div>
        )}
      </Upload>
      {hint && <div style={{ fontSize: 12, color: "#9E9E9E", marginTop: 6, maxWidth: 280 }}>{hint}</div>}
    </div>
  );
}
